import { useState } from "react";
import { Shield, Loader2, Check, AlertCircle } from "lucide-react";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminSetup() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [token, setToken] = useState("");
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setMsg("");
    try {
      const { data } = await axios.post(
        `${API_URL}/admin/reset-admin-password`,
        { email: email.trim(), new_password: pw },
        { headers: { "X-Admin-Setup-Token": token.trim() } }
      );
      setStatus("done");
      setMsg(data.created
        ? "Admin account created. Log in with your new password."
        : "Password reset. All previous sessions were revoked — log in again."
      );
    } catch (err) {
      setStatus("error");
      setMsg(err?.response?.data?.detail || err?.message || "Reset failed");
    }
  };

  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white p-4 md:p-12" data-testid="admin-setup-page">
      <div className="max-w-md mx-auto pt-20">
        <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3 flex items-center gap-1.5">
          <Shield className="w-3 h-3" /> Admin account setup
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tighter mb-3">
          Reset your admin password
        </h1>
        <p className="text-sm text-zinc-400 mb-8 leading-relaxed">
          Only emails in <span className="text-white font-mono">ADMIN_EMAILS</span> can use this
          form. You need the setup token from <span className="text-white font-mono">backend/.env</span>
          (<span className="text-white font-mono">ADMIN_SETUP_TOKEN</span>).
        </p>

        <form onSubmit={submit} className="space-y-3">
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block">Admin email</label>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="admin-setup-email"
            className="w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
          />
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block pt-2">New password (≥ 8 chars)</label>
          <input
            type="password"
            required
            minLength={8}
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            data-testid="admin-setup-password"
            className="w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
          />
          <label className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 block pt-2">Setup token</label>
          <input
            type="password"
            required
            value={token}
            onChange={(e) => setToken(e.target.value)}
            data-testid="admin-setup-token"
            placeholder="ADMIN_SETUP_TOKEN from backend/.env"
            className="w-full h-11 bg-zinc-950 border border-zinc-800 px-3 text-zinc-100 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
          />
          <button
            type="submit"
            disabled={status === "loading"}
            data-testid="admin-setup-submit"
            className="w-full h-11 mt-4 bg-white text-zinc-950 hover:bg-zinc-200 font-semibold text-sm disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
            Reset password
          </button>
        </form>

        {status === "done" && (
          <div className="mt-4 border border-emerald-500/30 bg-emerald-500/5 text-emerald-300 text-sm p-3 flex items-start gap-2" data-testid="admin-setup-success">
            <Check className="w-4 h-4 mt-0.5" /> <span>{msg}</span>
          </div>
        )}
        {status === "error" && (
          <div className="mt-4 border border-red-500/30 bg-red-500/5 text-red-300 text-sm p-3 flex items-start gap-2" data-testid="admin-setup-error">
            <AlertCircle className="w-4 h-4 mt-0.5" /> <span>{msg}</span>
          </div>
        )}
      </div>
    </div>
  );
}
